import {
  FetchStatus,
  type ParsedResumeConversion,
  type ResumeGlowupData,
  type ResumeGlowupDataContextType,
  SectionDecision,
} from "./resumeGlowupTypes";

import { createContext, useCallback, useContext } from "react";

export const initialResumeGlowupData: ResumeGlowupData = {
  hasOptedIn: false,
  sectionDecisions: {
    summary: SectionDecision.Pending,
    skills: SectionDecision.Pending,
    workExperiences: {}, // Populated dynamically based on work experience IDs
  },
  fetchStatus: {
    originalResume: FetchStatus.Pending,
    aiSuggestions: FetchStatus.Pending,
  },
  deletedOriginalSkillIds: [],
  userAddedSkills: [],
  userEditedWorkExperiences: {},
  userAddedWorkExperiences: [],
  // originalResume and aiImprovements start as undefined
};

export const ResumeGlowupDataContext = createContext<ResumeGlowupDataContextType>({
  resumeGlowupData: initialResumeGlowupData,
  setResumeGlowupData: (resumeGlowupData) => {
    console.error(
      "Trying to set resume glowup data on uninitialized context. Please ensure context data provider is initialized in the React tree.",
      resumeGlowupData,
    );
  },
  isEditFlowEnabled: false,
});

/**
 * Hook to track when user opts in to resume review.
 * Sets hasOptedIn to true, which triggers adding the review page to routes.
 */
export const useSetResumeGlowupOptIn = () => {
  const { setResumeGlowupData } = useContext(ResumeGlowupDataContext);

  return useCallback(() => {
    setResumeGlowupData((prev) => ({
      ...prev,
      hasOptedIn: true,
    }));
  }, [setResumeGlowupData]);
};

export const useIsEditFlowEnabled = (): boolean => {
  const { isEditFlowEnabled } = useContext(ResumeGlowupDataContext);
  return isEditFlowEnabled;
};

/**
 * Hook to reset resume glowup state (e.g., when navigating back).
 */
export const useResetResumeGlowupData = () => {
  const { setResumeGlowupData } = useContext(ResumeGlowupDataContext);

  return useCallback(() => {
    setResumeGlowupData(initialResumeGlowupData);
  }, [setResumeGlowupData]);
};

/**
 * Hook to read the full resume glowup data from context.
 */
export const useGetResumeGlowupData = () => {
  const { resumeGlowupData } = useContext(ResumeGlowupDataContext);
  return resumeGlowupData;
};

/**
 * Hook to get the setter function directly.
 */
export const useSetResumeGlowupData = () => {
  const { setResumeGlowupData } = useContext(ResumeGlowupDataContext);
  return setResumeGlowupData;
};

/**
 * Hook to set the original resume data.
 */
export const useSetOriginalResume = () => {
  const { setResumeGlowupData } = useContext(ResumeGlowupDataContext);

  return useCallback(
    (originalResume: ResumeGlowupData["originalResume"]) => {
      setResumeGlowupData((prev) => ({
        ...prev,
        originalResume,
      }));
    },
    [setResumeGlowupData],
  );
};

/**
 * Hook to set the AI improvements data.
 */
export const useSetAiImprovements = () => {
  const { setResumeGlowupData } = useContext(ResumeGlowupDataContext);

  return useCallback(
    (aiImprovements: ResumeGlowupData["aiImprovements"]) => {
      setResumeGlowupData((prev) => ({
        ...prev,
        aiImprovements,
      }));
    },
    [setResumeGlowupData],
  );
};

/**
 * Hook to set the fetch status for a specific query.
 */
export const useSetFetchStatus = () => {
  const { setResumeGlowupData } = useContext(ResumeGlowupDataContext);

  return useCallback(
    (key: keyof ResumeGlowupData["fetchStatus"], status: FetchStatus) => {
      setResumeGlowupData((prev) => ({
        ...prev,
        fetchStatus: {
          ...prev.fetchStatus,
          [key]: status,
        },
      }));
    },
    [setResumeGlowupData],
  );
};

// Re-export types
export type { ParsedResumeConversion, ResumeGlowupData, ResumeGlowupDataContextType };
